import React, { useEffect, useMemo, useState } from "react";
import "./BagsPurses.css";
import { Link } from "react-router-dom";
import { getSupabaseProductsByCategories } from "../lib/productService";
import { supabase } from "../lib/supabase";
import { useCartWishlist } from "../contexts/CartWishlistContext";
import Pagination from "../components/Pagination";

const formatINR = (amount) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);

const PER_PAGE = 12;

// Set to true when client adds Bags products via Dashboard
const SHOW_BAGS_PRODUCTS = true;

export default function BagsPurses() {
  const { addToCart, incQty, decQty, getQty, toggleWish, isWished } = useCartWishlist();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (SHOW_BAGS_PRODUCTS) {
      supabase.from("categories")
        .select("*")
        .then(({ data: allCats }) => {
          const validCatNames = new Set();
          const subCategoryNames = new Set();
          const subSubMap = {}; // Maps subCategory name to array of subSubCategory names
          
          if (allCats) {
            const mainBag = allCats.find(c => c.name === "Bags & Purses");
            if (mainBag) {
              const subCats = allCats.filter(c => c.parent_id === mainBag.id);
              subCats.forEach(sub => {
                validCatNames.add(sub.name);
                subCategoryNames.add(sub.name);
                subSubMap[sub.name] = [];
                
                const subSubCats = allCats.filter(c => c.parent_id === sub.id);
                subSubCats.forEach(subSub => {
                  validCatNames.add(subSub.name);
                  subSubMap[sub.name].push(subSub.name);
                });
              });
            }
            
            allCats.forEach(c => {
              if (c.description === 'Bags Sub-category' && !subCategoryNames.has(c.name)) {
                validCatNames.add(c.name);
                subCategoryNames.add(c.name);
                subSubMap[c.name] = [];
                
                const subSubCats = allCats.filter(child => child.parent_id === c.id);
                subSubCats.forEach(subSub => {
                  validCatNames.add(subSub.name);
                  subSubMap[c.name].push(subSub.name);
                });
              }
            });
          }
          
          const catList = Array.from(subCategoryNames).sort((a, b) => a.localeCompare(b));
          setCategories(catList);
          
          // Store the mapping so we know which products belong to which tab
          setCategoryTree(subSubMap);
          
          return getSupabaseProductsByCategories([...Array.from(validCatNames), "Bags & Purses"]);
        })
        .then((list) => {
          const sortedList = list.sort((a, b) => (a.category || "Other").localeCompare(b.category || "Other"));
          setProducts(
            sortedList.map((p) => ({
              id: p.id,
              name: p.title,
              price: p.offerPrice,
              img: p.images?.[0],
              link: `/product/${p.slug || p.id}`,
              isBestseller: p.isBestseller,
              category: p.category || "Bags & Purses",
            }))
          );
        })
        .finally(() => setLoading(false));
    } else {
      setProducts([]);
      setLoading(false);
    }
  }, []);

  const filteredProducts = useMemo(() => {
    if (activeTab === "all") return products;
    const allowedCategories = new Set([activeTab, ...(categoryTree[activeTab] || [])]);
    return products.filter(p => allowedCategories.has(p.category));
  }, [products, activeTab, categoryTree]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filteredProducts.slice(start, start + PER_PAGE);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / PER_PAGE) || 1;

  const paginatedItemsByCategory = useMemo(() => {
    const groups = [];
    let lastCat = null;
    paginatedProducts.forEach((item) => {
      const cat = item.category === "Bags & Purses" ? "From Our Collection" : item.category;
      if (cat !== lastCat) {
        groups.push({ category: cat, items: [] });
        lastCat = cat;
      }
      groups[groups.length - 1].items.push(item);
    });
    return groups;
  }, [paginatedProducts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const toggleWishlist = (item) => {
    toggleWish({ ...item, link: item.link || `/${item.id}` });
  };

  const renderCard = (item) => {
    const qty = getQty(item.id);
    const wished = isWished(item.id);
    return (
      <Link key={item.id} to={item.link} className="bCardLink">
        <article className="bCard" tabIndex={0}>
          <div className="bMedia">
            {item.isBestseller && <div className="bRibbon">Bestseller</div>}
            <img
              className="bImg"
              src={item.img || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f3f3' width='200' height='200'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E"}
              alt={item.name}
            />
          </div>
          <div className="bInfo">
            <h3 className="bName">{item.name}</h3>
            <div className="bMeta">
              <div className="bPrice">₹ {formatINR(item.price)}</div>
              <button
                className={`bWish ${wished ? "active" : ""}`}
                type="button"
                aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleWishlist(item);
                }}
              >
                {wished ? "♥" : "♡"}
              </button>
            </div>
            {qty === 0 ? (
              <button
                className="bAddCart"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToCart(item);
                }}
              >
                Add to Cart
              </button>
            ) : (
              <div className="bQtyWrap" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <button
                  className="bQtyBtn"
                  type="button"
                  aria-label="Decrease"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); decQty(item.id); }}
                >
                  −
                </button>
                <span className="bQtyNum">{qty}</span>
                <button
                  className="bQtyBtn"
                  type="button"
                  aria-label="Increase"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); incQty(item.id); }}
                >
                  +
                </button>
              </div>
            )}
          </div>
        </article>
      </Link>
    );
  };

  return (
    <div className="bagsPage">
      <section className="bSection">
        <div className="bWrap">
          <h2 className="bTitle">Bags &amp; Purses</h2>

          {loading ? (
            <div className="bComingSoon">
              <p className="bComingSoonNote">Loading...</p>
            </div>
          ) : (
            <div className="bSubSection">
              {categories.length > 0 && (
                <div className="jFilters" role="tablist" style={{ marginBottom: '40px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button
                    className={`jFilterBtn ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    All
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      className={`jFilterBtn ${activeTab === cat ? 'active' : ''}`}
                      onClick={() => setActiveTab(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              {paginatedProducts.length > 0 ? (
                <>
                  {paginatedItemsByCategory.map(({ category, items }) => (
                    <div key={category} style={{ marginBottom: '40px' }}>
                      <div className="bSubHead">
                        <h3 className="bSubTitle">{category}</h3>
                        <div className="bSubLine" />
                      </div>
                      <div className="bGrid">{items.map(renderCard)}</div>
                    </div>
                  ))}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </>
              ) : (
                <div className="bComingSoon">
                  <p className="bComingSoonTitle">Coming Soon</p>
                  <p className="bComingSoonNote">
                    We're curating products for {activeTab === 'all' ? 'this collection' : `the ${activeTab} category`}. Check back later!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
